import moment from "moment";
import { ADMIN, SUPPORT } from "config";

/** Weeks after which timesheet updates require Support or Admin (#1348). */
export const HISTORICAL_TIMESHEET_EDIT_WEEKS = 3;

/** Calendar-day equivalent of {@link HISTORICAL_TIMESHEET_EDIT_WEEKS} (3 × 7). */
export const HISTORICAL_TIMESHEET_EDIT_DAYS = HISTORICAL_TIMESHEET_EDIT_WEEKS * 7;

/**
 * How far back Support/Admin may select timesheet weeks on the calendar / dialog
 * (matches FullCalendar eventConstraint for historical editors).
 */
export const HISTORICAL_TIMESHEET_PICKER_MONTHS = 3;

/**
 * True when the timesheet date is more than 3 weeks (21 calendar days) before the reference day.
 * Exactly 21 calendar days old is not historical (managers retain existing edit rules).
 */
export const isTimesheetOlderThanThreeWeeks = (
  activityDate,
  referenceDate = new Date()
) => {
  if (activityDate == null || activityDate === "") return false;
  const activityDay = moment(activityDate).startOf("day");
  if (!activityDay.isValid()) return false;
  const refDay = moment(referenceDate).startOf("day");
  if (!refDay.isValid()) return false;
  return refDay.diff(activityDay, "days") > HISTORICAL_TIMESHEET_EDIT_DAYS;
};

/** @deprecated Use {@link isTimesheetOlderThanThreeWeeks} — boundary is 3 weeks. */
export const isTimesheetOlderThanTwoWeeks = isTimesheetOlderThanThreeWeeks;

/** IM Time Support and Admin may update historical (older than 3 weeks) timesheets. */
export const canUpdateHistoricalTimesheetByRole = (imtRole) => {
  return imtRole === ADMIN || imtRole === SUPPORT;
};

/**
 * Earliest week-start date the user may select/edit for timesheets.
 * Supervisor / managers: last {@link HISTORICAL_TIMESHEET_EDIT_WEEKS} weeks.
 * Support / Admin: last {@link HISTORICAL_TIMESHEET_PICKER_MONTHS} months.
 */
export const getEarliestSelectableTimesheetWeekStart = (
  imtRole,
  referenceDate = new Date()
) => {
  const ref = moment(referenceDate);
  if (!ref.isValid()) {
    return moment().startOf("week").toDate();
  }
  if (canUpdateHistoricalTimesheetByRole(imtRole)) {
    return ref
      .clone()
      .subtract(HISTORICAL_TIMESHEET_PICKER_MONTHS, "months")
      .startOf("week")
      .toDate();
  }
  return ref
    .clone()
    .subtract(HISTORICAL_TIMESHEET_EDIT_WEEKS, "weeks")
    .startOf("week")
    .toDate();
};

/**
 * Whether the user may update a timesheet for the given activity/period date.
 * Within 3 weeks (inclusive of exactly 21 calendar days): allowed at this layer
 * for any role. Support's recent read-only rule is enforced by
 * {@link isTimesheetEditRestrictedByRole} (UI + AddDialog/SplitDialog save guards).
 * Older than 3 weeks: only Support or Admin.
 */
export const canUpdateTimesheetForDate = (
  activityDate,
  imtRole,
  referenceDate = new Date()
) => {
  if (!isTimesheetOlderThanThreeWeeks(activityDate, referenceDate)) {
    return true;
  }
  return canUpdateHistoricalTimesheetByRole(imtRole);
};

/**
 * Date used for historical edit authorization in AddDialog.
 * Prefer {@code activityStart} so Support unlock for older-than-3-weeks timesheets
 * is not blocked when the date picker still holds its default (today) before the
 * activity sync effect runs.
 */
export const resolveTimesheetAuthDate = ({
  activityStart,
  selectStartDate,
  startDate,
  selectedWeek,
  fallbackDate,
} = {}) =>
  activityStart ||
  selectStartDate ||
  startDate ||
  selectedWeek ||
  fallbackDate ||
  null;

/**
 * UI edit restriction for the timesheet Add/Edit dialog.
 * Support: read-only unless the activity date OR the selected week is older than
 * 3 weeks (aligns dialog unlock with calendar week interactivity).
 * Other roles: blocked when the activity date is historical and role is not
 * Support/Admin.
 *
 * @param {Date|string|null} activityDate - activity / picker date
 * @param {string} imtRole
 * @param {Date} [referenceDate]
 * @param {Date|string|null} [weekDate] - calendar week start (selectedWeek)
 */
export const isTimesheetEditRestrictedByRole = (
  activityDate,
  imtRole,
  referenceDate = new Date(),
  weekDate = null
) => {
  const isHistoricalActivity = isTimesheetOlderThanThreeWeeks(
    activityDate,
    referenceDate
  );
  const isHistoricalWeek =
    weekDate != null &&
    weekDate !== "" &&
    isTimesheetOlderThanThreeWeeks(weekDate, referenceDate);
  const isHistoricalPeriod = isHistoricalActivity || isHistoricalWeek;

  // Support may edit only in a historical period (activity or week > 3 weeks).
  if (imtRole === SUPPORT && !isHistoricalPeriod) {
    return true;
  }
  // Non-Support/Admin cannot edit when the activity itself is historical.
  if (
    isHistoricalActivity &&
    !canUpdateHistoricalTimesheetByRole(imtRole)
  ) {
    return true;
  }
  return false;
};

/**
 * Calendar week interactivity for Timesheet FullCalendar.
 * Within 3 weeks: managers and other roles may interact (subject to other UI rules).
 * Older than 3 weeks: Support/Admin only.
 */
export const canInteractWithTimesheetWeek = (
  weekDate,
  imtRole,
  referenceDate = new Date()
) => {
  if (isTimesheetOlderThanThreeWeeks(weekDate, referenceDate)) {
    return canUpdateHistoricalTimesheetByRole(imtRole);
  }
  return true;
};
